<script setup>
import { 
	ref,
	computed, 
	onMounted,
} from 'vue'

import { useRouter } from 'vue-router'
import  { VehicleService } from '@/services/vehicle-service'
import { UserService } from '@/services/user-service'
import { useVehicleStore, storeReady } from '@/stores/vehicle-store'
import { mdiCar, mdiMagnify, mdiIdentifier, mdiSteering, mdiClose } from '@mdi/js'
import NavBarActions from '@/components/layout/NavBarActions.vue'
import { Notify } from '@/modules/util/notify'

const vehicleList = ref(null)
const vehicleService = new VehicleService
const router = useRouter()
const store = useVehicleStore()

const vehicles = ref([])
const search = ref('')
const vin    = ref('')
const id = ref('')
const showInspectNew = ref(false)
const showSkipLookup = ref(false)
const showFoundOffsite = ref(false)
const addInProgress = ref(false)
const hoursTilSale = ref(36)

const addManual = ref({
	registration: '',
	manufacturer: '',
	model: '',
	variant: '',
	colour: '',
	classification: null,
	mileage: '',
	denominator: 'Miles',
	warranted: '',
})
const classifications = ref([])
const valid = ref(false)
const rules = {
	required: val => !!val || 'Required',
}

const Filters = Object.freeze({
	NEW: 'New vehicles',
	SELLING_SOON: 'Selling soon',
	INSPECTION_REQUIRED: 'Inspection required',
})
const filterBy = ref(Filters.DEFAULT)

const filteredVehicles = computed(() => {
	if (!search.value && !filterBy.value) {
		return vehicles.value
	}

	let result = [ ...vehicles.value ]

	if (filterBy.value) {
		result = vehicles.value.filter(vehicle => {
			if (filterBy.value === Filters.NEW) {
				return new Date(vehicle.created) > (new Date() - 1000 * 60 * 60 * 24 * 28)
			}

			if (filterBy.value === Filters.SELLING_SOON) {
				if (!vehicle.auction_datetime) return false
				
				const sale = new Date(vehicle.auction_datetime)
				const cutoff = new Date(Date.now() + 1000 * 60 * 60 * hoursTilSale.value)

				return sale < cutoff
			}

			if (filterBy.value === Filters.INSPECTION_REQUIRED) {
				if (!vehicle.inspection_types) return false
				if (vehicle.inspection_types == '' ) return false
				return true
                
			}

			return true
		})
	}

	if (search.value) {
		result = result.filter(vehicle => {
			const compare = `${vehicle.manufacturer} ${vehicle.model} ${vehicle.variant} ${vehicle.registration} ${vehicle.registration.replace(' ', '')}` 
			return compare.toLowerCase().includes(search.value.toLowerCase())
		})
	}

	if (filterBy.value === Filters.SELLING_SOON) {
		result.sort((a, b) => a.auction_datetime <= b.auction_datetime ? 1 : -1)
	}

	return result
})

const listVehicles = async () => {
	await storeReady

	const promise = vehicleService.list().then(res => {
		if (!(res?.body?.status && res.body.vehicles)) {
			throw new Error(JSON.stringify(res))
		}
        
		vehicles.value = res.body.vehicles
		store.setVehicles(vehicles.value)
	}).catch(e => {
		console.warn('listVehicles:', e.message || e)

		if (UserService.hasAuth()) {
			Notify.warning('Could not refresh vehicle list. You may have poor connectivity')
		}
	})

	vehicles.value = store.allVehicles || []

	if (!vehicles.value.length) {
		await promise
	}
}

const listClassifications = async () => {
	await storeReady
            
	const promise = vehicleService.classifications().then(res => {
		if (!(res?.body?.status && res.body.classifications)) {
			throw new Error(JSON.stringify(res))
		}

		classifications.value = res.body.classifications
		store.setClassifications(classifications.value)
	}).catch(e => {
		console.warn('listClassifications:', e.message || e)
	})

	classifications.value = store.classifications || []

	if (!classifications.value.length) {
		await promise
	}
}

const findVehicle = async (forceCreate = false) => {
	try {
		addInProgress.value = true

		let func = 'vrmLookup', val = search.value
		if (vin.value) {
			func = 'vinLookup'
			val = vin.value
		}

		const res = await vehicleService[func](val, forceCreate)	

		if (!(res.body?.status && res.body?.vehicle)) {
			throw new Error(JSON.stringify(res.body))
		}

		vehicles.value.push(res.body.vehicle)
		store.setVehicles(vehicles.value)
		store.setCurrentVehicle(res.body.vehicle)

		if (res.body.offsited) {
			showFoundOffsite.value = true
		} else {
			router.push({ name: 'get-vehicle', params: { id: res.body.vehicle.id } })
		}
	} catch (e) {
		console.error('findVehicle:', e.message)
		Notify.error('VRM lookup failed')
	} finally {
		addInProgress.value = false
	}
}

const tryAddManual = async () => {
	try {
		addInProgress.value = true

		const res = await vehicleService.addManual(addManual.value)

		if (!(res.body?.status && res.body?.id)) {
			throw new Error(JSON.stringify(res.body))
		}

		router.push({ name: 'get-vehicle', params: { id: res.body.id } })
	} catch (err) {
		console.error('tryAddManual:', err.message)
		Notify.error('Failed to manually add vehicle')
	} finally {
		addInProgress.value = false
	}
}

const calcListHeight = () => {
	if (vehicleList.value) {
		vehicleList.value.style.height = `${window.innerHeight - vehicleList.value.getBoundingClientRect().top}px`
	}
}

onMounted(() => {
	listVehicles()
	listClassifications()
	calcListHeight()
	window.addEventListener('resize', calcListHeight)
})
</script>

<template>
<v-row>
	<v-col>
		<v-btn-toggle
			v-model="filterBy"
			rounded="0"
			class="w-100 h-100 d-flex flex-wrap"
			color="secondary"
		>
			<v-btn 
				v-for="label in Object.values(Filters)" 
				:key="label" 
				:value="label"
				class="flex-grow-1"
				style="min-height: 48px"
			>
				{{ label || 'All' }}	
			</v-btn>
		</v-btn-toggle>
	</v-col>
</v-row>
<div
	ref="vehicleList"
	style="display: flex; flex-direction: column;"
>
	<v-list-item
		v-if="filterBy === Filters.SELLING_SOON"
		class="pa-0"
	>
		<v-select
			v-model="hoursTilSale"
			:items="[{ title: '24 hours', value: 24 }, { title: '36 hours', value: 36 }, { title: '48 hours', value: 48 }, { title: '72 hours', value: 72 }]"
		/>
	</v-list-item>

	<!-- The v-if fixes a vuetify bug: virtual-scroll does not properly re-render if previous items were empty -->
	<v-virtual-scroll
		v-if="filteredVehicles.length"	
		:items="filteredVehicles"
		item-height="60"
	>
		<template #default="{ item: vehicle }">
			<v-list-item
				:key="vehicle.id"
				lines="two"
				@click.stop="router.push({ name: 'get-vehicle', params: { id: vehicle.id }})"
			>
				<v-list-item-title>{{ vehicle.registration }}</v-list-item-title>
				<v-list-item-subtitle>{{ `${vehicle.manufacturer} ${vehicle.model} ${vehicle.variant}` }}</v-list-item-subtitle>
			</v-list-item>
		</template>
	</v-virtual-scroll>
	<v-list-item>
		<v-btn
			class="my-1 w-100"
			color="primary"
			:variant="filteredVehicles.length ? 'tonal' : undefined"
			@click="showInspectNew = true"
		>
			Lookup Vehicle
		</v-btn>
	</v-list-item>
</div>

<v-dialog v-model="showInspectNew">
	<v-card
		:title="showSkipLookup ? 'Add Vehicle' : 'Vehicle Search'"
		class="mx-auto"
		:max-width="showSkipLookup ? 1000 : 600"
	>
		<v-card-text
			v-if="!showSkipLookup"
			class="d-flex flex-column"
		>
			<div class="lookup-label">
				VRM Lookup
			</div>
			<div
				class="d-flex flex-row align-center"
				style="gap: 8px"
			>
				<v-text-field
					v-model="search"
					variant="underlined"
					density="comfortable"
					:prepend-inner-icon="mdiCar"
					placeholder="AB12 3CD"
					hide-details
				/>
				<v-btn
					color="primary"
					size="small"
					:loading="addInProgress"
					@click="() => findVehicle()"
				>
					Go
				</v-btn>
			</div>
			<div class="lookup-label">
				VIN Lookup
			</div>
			<div 
				class="d-flex flex-row align-center" 
				style="gap:8px"
			>
				<v-text-field
					v-model="vin"
					variant="underlined"
					density="comfortable"
					:prepend-inner-icon="mdiSteering"
					placeholder="PN1LFNT328"
					hide-details
				/>
				<v-btn
					color="primary"
					size="small"
					:loading="addInProgress"
					@click="() => findVehicle()"
				>
					Go
				</v-btn>
			</div>
			<div class="lookup-label">
				Record ID
			</div>
			<div
				class="d-flex flex-row align-center"
				style="gap: 8px"
			>
				<v-text-field
					v-model="id"
					variant="underlined"
					density="comfortable"
					:prepend-inner-icon="mdiIdentifier"
					placeholder="99999"
					hide-details
				/>
				<v-btn
					color="primary"
					size="small"
					:loading="addInProgress"
					@click="router.push({ name: 'get-vehicle', params: { id } })"
				>
					Go
				</v-btn>
			</div>
		</v-card-text>
		<v-card-text v-else>
			<v-form
				v-model="valid"
				@submit.prevent="tryAddManual"
			>
				<v-row>
					<v-col
						cols="12"
						sm="6"
					>
						<v-text-field
							v-model="addManual.registration"
							label="Registration"
						/>
					</v-col>
					<v-col
						cols="12"
						sm="6"
					>
						<v-select
							v-model="addManual.classification"
							label="Classification *"
							:items="classifications"
							item-value="id"
							item-title="title"
							:rules="[rules.required]"
						/>
					</v-col>
					<v-col
						cols="12"
						sm="6"
					>
						<v-text-field
							v-model="addManual.manufacturer"
							label="Manufacturer"
						/>
					</v-col>
					<v-col
						cols="12"
						sm="6"
					>
						<v-text-field
							v-model="addManual.model"
							label="Model"
						/>
					</v-col>
					<v-col
						cols="12"
						sm="6"
					>
						<v-text-field
							v-model="addManual.variant"
							label="Variant"
						/>
					</v-col>
					<v-col
						cols="12"
						sm="6"
					>
						<v-text-field
							v-model="addManual.colour"
							label="Colour"
						/>
					</v-col>
					<v-col
						cols="12"
						class="d-flex flex-row align-baseline"
					>
						<v-text-field
							v-model="addManual.mileage"
							label="Mileage"
							type="number"
							class="me-4"
						/>
						<v-select
							v-model="addManual.denominator"
							:items="['Miles', 'Km', 'Hours']"
						/>
					</v-col>
					<v-col
						cols="12"
						sm="6"
					>
						<v-select 
							v-model="addManual.warranted"
							label="Warranted?"
							:items="[{ title: 'Yes', value: '1' }, { title: 'No', value: '0' }, { title: 'Incorrect', value: '-1' }, { title: 'Not Showing', value: '-2' }, { title: 'TBC', value: '-3' }]"
						/>
					</v-col>
				</v-row>
				<v-row>
					<v-col cols="12">
						<v-btn
							type="submit"
							color="primary"
							:disabled="!valid"
							:loading="addInProgress"
						>
							Add
						</v-btn>
					</v-col>
				</v-row>
			</v-form>
		</v-card-text>
		<v-card-actions>
			<v-spacer />
			<v-btn
				color="error"
				@click="showInspectNew = false; showSkipLookup = false"
			>
				Cancel
			</v-btn>
			<v-btn @click="showSkipLookup = !showSkipLookup">
				{{ showSkipLookup ? '' : 'Skip ' }}lookup
			</v-btn>
		</v-card-actions>
	</v-card>
</v-dialog>

<v-dialog v-model="showFoundOffsite">
	<v-card
		class="mx-auto"
		:max-width="600"
		title="Existing Vehicle Record Found"
	>
		<v-card-text>
			A record of this vehicle already exists, but it is marked as offsite.
			<br>
			Open the existing record or create a new one?
		</v-card-text>
		<v-card-actions>
			<v-btn
				class="me-auto"
				color="error"
				@click="showFoundOffsite = false"
			>
				<v-icon :icon="mdiClose" />
			</v-btn>
			<v-btn
				color="primary"
				variant="tonal"
				@click="() => {
					showFoundOffsite = false
					router.push({ name: 'get-vehicle', params: { 
						id: store.currentVehicle.id
					} })
				}"
			>
				Open Existing Record
			</v-btn>
			<v-btn
				color="success"
				variant="tonal"
				@click="() => {
					showFoundOffsite = false
					findVehicle(true)
				}"
			>
				Create New Record
			</v-btn>
		</v-card-actions>
	</v-card>
</v-dialog>

<NavBarActions>
	<div style="width: 200px;">
		<v-text-field
			v-model="search"
			variant="solo"
			density="compact"
			:prepend-inner-icon="mdiMagnify"
			placeholder="Search"
			single-line
			hide-details
		/>
	</div>
</NavBarActions>
</template>

<style lang="scss" scoped>
.lookup-label {
	text-transform: uppercase;
	font-size: .6rem;
	font-weight: 400;
	letter-spacing: 0.15;
	margin-top: 2rem;
}
.lookup-label:first-of-type {
	margin-top: 0;
}
</style>